import { keyframes } from '@emotion/react';
import { useQuery } from '@tanstack/react-query';

import React, { useCallback, useEffect, useState } from 'react';

import { CheckCircle, Fullscreen, RadioButtonUnchecked, Replay } from '@mui/icons-material';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	LinearProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Paper,
	Stack,
	Tooltip,
	Typography,
	useMediaQuery,
} from '@mui/material';

import { EnvironmentClient, ReleasePlanStatus } from '../../../api/api';
import { formatTimestampSortable } from '../../../common/formatTimestamp';
import useApiClient from '../../../common/hooks/useApiClient';
import useToastService from '../../../common/hooks/useToastService';
import { CopyToClipboardButton, useNotificationRefetch, useNotifications } from '../../../components';
import type { ReleasePlanEventNotification } from '../../../components';
import DialogHeaderActions from '../../../components/DialogHeaderActions';
import SidePanelDetail from '../../../components/Panels/SidePanelDetail';
import ReleasePlanProgressText, { releasePlanProgressToPlainText } from './ReleasePlanProgressText';
import { RELEASE_PLANS_PANEL_COLLAPSED_WIDTH, RELEASE_PLANS_PANEL_DEFAULT_WIDTH } from './ReleasePlansPanel';

const RELEASE_PLAN_DETAILS_PANEL_STORAGE_KEY = 'releasePlanDetailsPanel.width';
const RELEASE_PLAN_DETAILS_PANEL_DEFAULT_WIDTH = 500;
const RELEASE_PLAN_DETAILS_PANEL_MIN_WIDTH = 360;
const RELEASE_PLAN_DETAILS_PANEL_MAX_WIDTH = 1000;

const taskReveal = keyframes`
	from { opacity: 0; transform: translateY(-4px); }
	to { opacity: 1; transform: translateY(0); }
`;

const activeStepPulse = keyframes`
	0% {
		box-shadow: 0 0 0 0 rgba(41, 182, 246, 0.8);
		transform: scale(1);
	}
	65% {
		box-shadow: 0 0 0 11px rgba(41, 182, 246, 0);
		transform: scale(1.3);
	}
	100% {
		box-shadow: 0 0 0 0 rgba(41, 182, 246, 0);
		transform: scale(1);
	}
`;

interface ReleasePlanDetailsPanelProps {
	releasePlanId: string;
	onClose: () => void;
	onStatusChange?: () => void;
	listPanelCollapsed?: boolean;
	rightOffset?: number;
	top?: number;
}

export default function ReleasePlanDetailsPanel({
	releasePlanId,
	onClose,
	onStatusChange,
	listPanelCollapsed = false,
	rightOffset = RELEASE_PLANS_PANEL_DEFAULT_WIDTH,
	top = 0,
}: ReleasePlanDetailsPanelProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
	const { latestReleasePlanEvent } = useNotifications();
	const [refreshKey, setRefreshKey] = useState(0);
	const [previousStatus, setPreviousStatus] = useState<ReleasePlanStatus | undefined>(undefined);
	const [panelWidth, setPanelWidth] = useState(RELEASE_PLAN_DETAILS_PANEL_DEFAULT_WIDTH);
	const toastService = useToastService();
	const [isRerunning, setIsRerunning] = useState(false);
	const [isErrorDetailsDialogOpen, setIsErrorDetailsDialogOpen] = useState(false);
	const [isErrorDetailsDialogMaximized, setIsErrorDetailsDialogMaximized] = useState(false);

	// Query to fetch release plan details
	const {
		data: releasePlan,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useQuery({
		queryKey: ['release-plan-details', releasePlanId, refreshKey],
		queryFn: async () => {
			return await client.getReleasePlanById(releasePlanId);
		},
	});

	useNotificationRefetch({
		notification: latestReleasePlanEvent,
		getKey: useCallback((event: ReleasePlanEventNotification) => `${event.payload.id}:${event.receivedAt}`, []),
		shouldRefetch: useCallback((event: ReleasePlanEventNotification) => event.payload.id === releasePlanId, [releasePlanId]),
		refetch,
	});

	// Track status changes and notify parent to refresh environment data
	useEffect(() => {
		if (releasePlan?.status && previousStatus !== undefined && previousStatus !== releasePlan.status) {
			// Status has changed, notify parent to refresh
			onStatusChange?.();
		}
		setPreviousStatus(releasePlan?.status);
	}, [releasePlan?.status, previousStatus, onStatusChange]);

	const handleRefresh = () => {
		setRefreshKey((prev) => prev + 1);
		refetch();
	};

	const handleRerun = async () => {
		setIsRerunning(true);
		try {
			await client.rerunReleasePlan(releasePlanId);
			toastService.success('Release plan rerun started.');
		} catch (error) {
			const message = error instanceof Error ? error.message : 'Unknown error';
			toastService.error(`Failed to rerun release plan: ${message}`);
			console.error('Error rerunning release plan:', error);
		} finally {
			setIsRerunning(false);
			setRefreshKey((prev) => prev + 1);
		}
	};

	const handleOpenErrorDetailsDialog = () => {
		setIsErrorDetailsDialogMaximized(false);
		setIsErrorDetailsDialogOpen(true);
	};

	const handleCloseErrorDetailsDialog = () => {
		setIsErrorDetailsDialogOpen(false);
		setIsErrorDetailsDialogMaximized(false);
	};

	const getStatusColor = (status?: ReleasePlanStatus) => {
		switch (status) {
			case ReleasePlanStatus.Completed:
				return 'success';
			case ReleasePlanStatus.InProgress:
				return 'info';
			case ReleasePlanStatus.Failed:
				return 'error';
			case ReleasePlanStatus.Pending:
			default:
				return 'default';
		}
	};

	const getStatusLabel = (status?: ReleasePlanStatus) => {
		switch (status) {
			case ReleasePlanStatus.Completed:
				return 'Completed';
			case ReleasePlanStatus.InProgress:
				return 'In Progress';
			case ReleasePlanStatus.Failed:
				return 'Failed';
			case ReleasePlanStatus.Pending:
			default:
				return 'Pending';
		}
	};

	const completedSteps = releasePlan?.completedStepCount || 0;
	const totalSteps = releasePlan?.steps?.length || 0;
	const steps = releasePlan?.steps || [];
	const errors = releasePlan?.errors || [];
	const isInProgress = releasePlan?.status === ReleasePlanStatus.InProgress;
	const progress = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
	const canRerun = releasePlan?.status === ReleasePlanStatus.Failed || releasePlan?.status === ReleasePlanStatus.Completed;
	const headerActions = canRerun ? (
		<Button size="small" variant="outlined" onClick={handleRerun} disabled={isRerunning} startIcon={isRerunning ? <CircularProgress size={14} /> : <Replay />}>
			Rerun
		</Button>
	) : null;

	const errorDetailsText = [releasePlan?.message, ...errors].filter(Boolean).join('\n');

	// Subtitle component with ID and copy button
	const subtitle = (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			<Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1, flexShrink: 1 }}>
				ID: {releasePlanId}
			</Typography>
			<CopyToClipboardButton value={releasePlanId} size="small" iconSize={14} title="Copy ID to clipboard" edge={false} sx={{ p: 0.5 }} />
		</Box>
	);

	return (
		<SidePanelDetail
			title="Release Plan Details"
			subtitle={subtitle}
			isLoading={isLoading}
			isFetching={isFetching}
			error={error as Error | null}
			errorMessage="Failed to load release plan details"
			headerActions={headerActions}
			onClose={onClose}
			onRefresh={handleRefresh}
			position="stacked"
			top={top}
			rightOffset={rightOffset}
			width={panelWidth}
			zIndex={1201}
			listPanelCollapsed={listPanelCollapsed}
			listPanelCollapsedWidth={RELEASE_PLANS_PANEL_COLLAPSED_WIDTH}
			resizeStorageKey={RELEASE_PLAN_DETAILS_PANEL_STORAGE_KEY}
			defaultWidth={RELEASE_PLAN_DETAILS_PANEL_DEFAULT_WIDTH}
			minWidth={RELEASE_PLAN_DETAILS_PANEL_MIN_WIDTH}
			maxWidth={RELEASE_PLAN_DETAILS_PANEL_MAX_WIDTH}
			setWidth={setPanelWidth}
		>
			{releasePlan && (
				<>
					<Box sx={{ mb: 2 }}>
						<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
							<Typography variant="body2" color="textSecondary">
								Status:
							</Typography>
							<Chip label={getStatusLabel(releasePlan.status)} color={getStatusColor(releasePlan.status)} size="small" />
						</Stack>

						<Box sx={{ mb: 1.5 }} aria-live="polite">
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
								<Typography variant="body2" color="textSecondary">
									Progress
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 600 }}>
									{completedSteps} of {totalSteps} steps completed
								</Typography>
							</Stack>
							<LinearProgress
								variant="determinate"
								value={progress}
								aria-label={`${completedSteps} of ${totalSteps} steps completed`}
								sx={{
									height: 7,
									borderRadius: 4,
									backgroundColor: 'action.hover',
									'& .MuiLinearProgress-bar': {
										borderRadius: 4,
										backgroundImage: isInProgress ? 'linear-gradient(90deg, #29b6f6 0%, #42a5f5 45%, #80d8ff 100%)' : undefined,
									},
									'@media (prefers-reduced-motion: reduce)': {
										'& .MuiLinearProgress-bar': { transition: 'none' },
									},
								}}
							/>
						</Box>

						<Stack direction="row" spacing={2} alignItems="center">
							<Typography variant="body2" color="textSecondary">
								Created:
							</Typography>
							<Typography variant="body2">{formatTimestampSortable(releasePlan.createdDate)}</Typography>
						</Stack>

						{errorDetailsText && (
							<Box sx={{ mt: 1.5 }}>
								<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
									<Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
										Error details ({errors.length})
									</Typography>
									<CopyToClipboardButton value={errorDetailsText} title="Copy error details" size="small" iconSize={14} edge={false} sx={{ p: 0.5 }} />
									<Tooltip title="Open full error details view">
										<IconButton size="small" onClick={handleOpenErrorDetailsDialog} sx={{ p: 0.5 }}>
											<Fullscreen fontSize="small" />
										</IconButton>
									</Tooltip>
								</Stack>

								<Alert severity="error" sx={{ alignItems: 'flex-start', mb: errors.length > 0 ? 1 : 0 }}>
									<Typography variant="body2" sx={{ mt: 0.25 }}>
										{releasePlan.message || 'Release plan errors occurred.'}
									</Typography>
								</Alert>
							</Box>
						)}
						<Dialog
							open={isErrorDetailsDialogOpen}
							onClose={handleCloseErrorDetailsDialog}
							fullScreen={isErrorDetailsDialogMaximized}
							fullWidth
							maxWidth={false}
							PaperProps={{
								sx: isErrorDetailsDialogMaximized
									? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', overflow: 'hidden' }
									: { width: 'min(720px, 95vw)', maxHeight: '90vh', overflow: 'hidden' },
							}}
						>
							<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
									Release Plan Error Details
								</Typography>
								<Box sx={{ flexGrow: 1 }} />
								<DialogHeaderActions
									isMaximized={isErrorDetailsDialogMaximized}
									onToggleMaximized={() => setIsErrorDetailsDialogMaximized((value) => !value)}
									onClose={handleCloseErrorDetailsDialog}
								>
									<CopyToClipboardButton value={errorDetailsText} title="Copy error details" size="small" edge={false} />
								</DialogHeaderActions>
							</DialogTitle>
							<DialogContent dividers sx={{ flex: 1, overflow: 'auto', '& .MuiDialogContent-root': { p: 0 } }}>
								<Alert severity="error" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
									<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
										{errorDetailsText || 'No error details available.'}
									</Typography>
								</Alert>
							</DialogContent>
							<DialogActions>
								<Button onClick={handleCloseErrorDetailsDialog}>Close</Button>
							</DialogActions>
						</Dialog>

						{errors.length > 0 && (
							<List dense sx={{ py: 0 }}>
								{errors.map((errorItem, errorIndex) => (
									<ListItem key={`${releasePlan?.id}-${errorIndex}`} sx={{ px: 0, alignItems: 'flex-start' }}>
										<ListItemIcon sx={{ minWidth: 24, mt: 0.2 }}>
											<RadioButtonUnchecked color="error" sx={{ fontSize: 10 }} />
										</ListItemIcon>
										<ListItemText
											primary={errorItem}
											sx={{
												'& .MuiListItemText-primary': {
													fontSize: '0.75rem',
													color: 'text.secondary',
													whiteSpace: 'pre-wrap',
													wordBreak: 'break-word',
												},
											}}
										/>
									</ListItem>
								))}
							</List>
						)}
					</Box>

					<Divider sx={{ my: 2 }} />

					{/* Steps List */}
					<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
							Release steps
						</Typography>
						<Chip label={`${completedSteps} / ${totalSteps}`} size="small" variant="outlined" color={completedSteps === totalSteps && totalSteps > 0 ? 'success' : 'default'} />
					</Stack>

					{steps.length === 0 ? (
						<Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
							No steps found for this release plan
						</Typography>
					) : (
						<Stack spacing={1.25}>
							{steps.map((step, index) => {
								const isCompleted = index < completedSteps;
								const isActive = isInProgress && index === completedSteps;
								const stepLabel = step.displayName || `Step ${index + 1}`;
								const stepCopyText = [
									releasePlanProgressToPlainText(stepLabel),
									...(step.tasks?.map((task) => releasePlanProgressToPlainText(task.displayName || '')).filter(Boolean) || []),
								].join('\n');
								const stepState = isCompleted ? 'Completed' : isActive ? 'In progress' : 'Upcoming';

								return (
									<Paper
										key={index}
										variant="outlined"
										sx={{
											p: 1.5,
											borderRadius: 2,
											borderColor: isActive ? 'info.main' : 'divider',
											bgcolor: isActive ? 'action.hover' : 'background.paper',
											boxShadow: isActive ? 1 : 0,
										}}
									>
										<Stack direction="row" spacing={1.25} alignItems="flex-start">
											<Box sx={{ pt: 0.2, minWidth: 24, display: 'flex', justifyContent: 'center' }}>
												{isCompleted ? (
													<CheckCircle color="success" sx={{ fontSize: 21 }} />
												) : isActive ? (
													<Box
														sx={{
															width: 10,
															height: 10,
															borderRadius: '50%',
															backgroundColor: 'info.main',
															boxShadow: '0 0 0 0 rgba(41, 182, 246, 0.8)',
															animation: `${activeStepPulse} 1.25s ease-out infinite`,
															willChange: 'transform, box-shadow',
														}}
													/>
												) : (
													<RadioButtonUnchecked
														color="disabled"
														sx={{
														fontSize: 21,
														}}
													/>
												)}
											</Box>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.35, flexWrap: 'wrap' }}>
													<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.35 }}>
														STEP {index + 1}
													</Typography>
													<Chip
														label={stepState}
														size="small"
														color={isCompleted ? 'success' : isActive ? 'info' : 'default'}
														variant={isCompleted || isActive ? 'filled' : 'outlined'}
														sx={{ height: 21, '& .MuiChip-label': { px: 0.9, fontSize: '0.68rem' } }}
													/>
												</Stack>
												<Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', overflowWrap: 'anywhere' }}>
													<ReleasePlanProgressText text={stepLabel} />
												</Typography>
											</Box>
											<CopyToClipboardButton
												value={stepCopyText}
												title={`Copy ${releasePlanProgressToPlainText(stepLabel)} and its tasks`}
												iconSize={15}
												edge={false}
												sx={{ color: 'text.secondary', mt: -0.5, mr: -0.5 }}
											/>
										</Stack>

										{/* Tasks under this step */}
										{step.tasks && step.tasks.length > 0 && (
											<Box sx={{ ml: 4.4, mt: 1.25, pl: 1.5, borderLeft: '2px solid', borderColor: 'divider' }}>
												<Stack key={releasePlanId}>
													{step.tasks.map((task, taskIndex) => (
														<Collapse key={taskIndex} in appear timeout={reduceMotion ? 0 : 280}>
														<Box
															sx={{
																mt: taskIndex === 0 ? 0 : 0.75,
																animation: `${taskReveal} 280ms ease-out`,
																'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
																position: 'relative',
																px: 1.25,
																py: 0.9,
																borderRadius: 1,
																bgcolor: 'action.hover',
																'&::before': {
																	content: '""',
																	position: 'absolute',
																	left: -12,
																	top: 14,
																	width: 8,
																	height: 2,
																	bgcolor: 'divider',
																},
															}}
														>
															<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.25 }}>
																<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
																	TASK {taskIndex + 1}
																</Typography>
																<CopyToClipboardButton
																	value={releasePlanProgressToPlainText(task.displayName || '')}
																	title={`Copy task ${taskIndex + 1}`}
																	size="small"
																	iconSize={14}
																	edge={false}
																	sx={{ p: 0.25, color: 'text.secondary', mr: -0.5, mt: -0.35 }}
																/>
															</Stack>
															<Typography
																variant="body2"
																sx={{
																	fontSize: '0.8rem',
																	color: 'text.secondary',
																	whiteSpace: 'pre-wrap',
																	lineHeight: 1.5,
																	wordBreak: 'break-word',
																}}
															>
																<ReleasePlanProgressText text={task.displayName || ''} />
															</Typography>
														</Box>
														</Collapse>
													))}
												</Stack>
											</Box>
										)}
									</Paper>
								);
							})}
						</Stack>
					)}
				</>
			)}
		</SidePanelDetail>
	);
}
